import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { endpoints } from "./api";
import { useAuth } from "./auth";

const STORE = "aigntx.viewas.pos";
const PAD = 8;

interface Pos {
  x: number;
  y: number;
}

function clamp(p: Pos, el: HTMLElement | null): Pos {
  const w = el?.offsetWidth ?? 320;
  const h = el?.offsetHeight ?? 40;
  return {
    x: Math.min(Math.max(PAD, p.x), Math.max(PAD, window.innerWidth - w - PAD)),
    y: Math.min(Math.max(PAD, p.y), Math.max(PAD, window.innerHeight - h - PAD)),
  };
}

function load(): Pos | null {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return null;
    const p = JSON.parse(raw) as Pos;
    return typeof p?.x === "number" && typeof p?.y === "number" ? p : null;
  } catch {
    return null;
  }
}

/**
 * The "you are viewing as someone else" marker.
 *
 * A floating pill rather than a banner: the app keeps its full height and its
 * own layout is untouched, and the pill can be dragged out of the way of
 * whatever you are looking at. Its position is remembered.
 */
export function ViewAsPill() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);

  // Start top-centre, or wherever it was left.
  useEffect(() => {
    if (!user?.viewedByEmail) return;
    const saved = load();
    const el = ref.current;
    const w = el?.offsetWidth ?? 340;
    setPos(clamp(saved ?? { x: Math.round((window.innerWidth - w) / 2), y: PAD }, el));
  }, [user?.viewedByEmail]);

  // Keep it on screen when the window changes size.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clamp(p, ref.current) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest("button")) return; // let the button click
      const el = ref.current;
      if (!el || !pos) return;
      drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false };
      el.setPointerCapture(e.pointerId);
    },
    [pos],
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    d.moved = true;
    setPos(clamp({ x: e.clientX - d.dx, y: e.clientY - d.dy }, ref.current));
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = drag.current;
      drag.current = null;
      ref.current?.releasePointerCapture(e.pointerId);
      if (d?.moved && pos) {
        try {
          localStorage.setItem(STORE, JSON.stringify(pos));
        } catch {
          /* a full quota is not worth failing over */
        }
      }
    },
    [pos],
  );

  async function stop() {
    await endpoints.viewAsStop();
    window.location.assign("/canvas");
  }

  if (!user?.viewedByEmail || !pos) return null;

  return (
    <div
      ref={ref}
      className="viewas-pill"
      role="status"
      style={{ left: pos.x, top: pos.y }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      title={t("viewAs.drag")}
    >
      <span className="viewas-pill__grip" aria-hidden="true">
        ⠿
      </span>
      <span className="viewas-pill__who">
        <strong>{user.name}</strong>
        <span className="viewas-pill__sub">
          {user.email} · {t("viewAs.by", { admin: user.viewedByEmail })}
        </span>
      </span>
      <button type="button" className="viewas-pill__exit" onClick={() => void stop()}>
        {t("viewAs.stop")}
      </button>
    </div>
  );
}
